/*
 * A Marvellous Deal: keep the free gift in the cart when it qualifies.
 *
 * The native Shopify automatic discount "A Marvellous Deal" makes the gift free when the
 * cart has >= the threshold of cakes. Shopify does NOT add the gift by itself, so this
 * script does: on page load and after every cart change it checks the cart and
 *   - adds 1 gift when the qualifying subtotal reaches the threshold,
 *   - removes the gift when the subtotal drops below it,
 *   - trims the gift back to 1 if a customer increases its quantity.
 *
 * Config comes from theme settings (Theme settings > A Marvellous Deal), not code:
 *   window.csmDeal = { enabled, giftVariantId, thresholdCents }
 * Replaces CartKing's auto-add. Only re-renders the cart's .js-contents areas, the same
 * way Dawn does on a quantity change, so the delivery date picker is never touched.
 *
 * Declining: a customer may remove the gift. The candle cart stores the cart token in
 * localStorage 'csm-gift-declined'; while it matches this cart, the gift is not re-added.
 * After any gift change this script fires a 'csm:deal-synced' event on document, so other
 * cart views (the candle cart section, header counts) can refresh themselves.
 */
(function () {
  const cfg = window.csmDeal || {};
  if (!cfg.enabled || !cfg.giftVariantId || !cfg.thresholdCents) return;

  const GIFT = Number(cfg.giftVariantId);
  const THRESHOLD = Number(cfg.thresholdCents);
  let busy = false;

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';

  async function getCart() {
    const res = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  }

  function declined(cart) {
    try {
      return !!cart.token && window.localStorage.getItem('csm-gift-declined') === cart.token;
    } catch (e) {
      return false;
    }
  }

  function qualifyingSubtotal(cart) {
    // Everything except the gift itself counts (all sellable cakes are in "Our Cakes").
    return cart.items
      .filter((item) => item.variant_id !== GIFT)
      .reduce((sum, item) => sum + item.original_line_price, 0);
  }

  async function post(url, body) {
    const res = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${url} ${res.status}`);
    return res.json();
  }

  async function rerenderCart() {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    const sections = ['cart-icon-bubble'];
    if (items && items.dataset.id) sections.push(items.dataset.id);
    if (footer && footer.dataset.id) sections.push(footer.dataset.id);
    const res = await fetch(`${cartUrl}?sections=${sections.join(',')}`);
    if (!res.ok) return;
    const html = await res.json();
    const parse = (s) => new DOMParser().parseFromString(s, 'text/html');

    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && html['cart-icon-bubble']) {
      const src = parse(html['cart-icon-bubble']).querySelector('.shopify-section');
      if (src) bubble.innerHTML = src.innerHTML;
    }
    [[items, items && items.dataset.id], [footer, footer && footer.dataset.id]].forEach(([el, id]) => {
      if (!el || !id || !html[id]) return;
      const src = parse(html[id]).querySelector(`#${el.id} .js-contents`);
      const dest = el.querySelector('.js-contents');
      if (src && dest) dest.innerHTML = src.innerHTML;
    });
    if (items) items.classList.toggle('is-empty', false);
  }

  // Bring the gift down/up to exactly `want` units. Shopify can split one variant into a
  // discounted line ($0) and a full-price line with DIFFERENT keys, and /cart/update by variant
  // only touches the first line, so fix line by line (keeping the free one) and re-check.
  async function setGiftQuantity(want) {
    for (let attempt = 0; attempt < 3; attempt++) {
      const cart = await getCart();
      const lines = cart.items
        .filter((item) => item.variant_id === GIFT)
        .sort((x, y) => x.final_line_price - y.final_line_price); // free line first
      const total = lines.reduce((n, item) => n + item.quantity, 0);
      if (total === want) return;
      if (total < want) {
        await post(addUrl, { items: [{ id: GIFT, quantity: want - total }] });
        continue;
      }
      let keep = want;
      for (const line of lines) {
        const target = Math.min(line.quantity, keep);
        keep -= target;
        if (target !== line.quantity) {
          await post(changeUrl, { id: line.key, quantity: target });
          break; // keys can change after a change; re-read the cart
        }
      }
    }
  }

  async function sync() {
    if (busy) return;
    busy = true;
    try {
      const cart = await getCart();
      const giftQty = cart.items
        .filter((item) => item.variant_id === GIFT)
        .reduce((n, item) => n + item.quantity, 0);
      const want = qualifyingSubtotal(cart) >= THRESHOLD && !declined(cart) ? 1 : 0;
      if (giftQty !== want) {
        await setGiftQuantity(want);
        await rerenderCart();
        document.dispatchEvent(new CustomEvent('csm:deal-synced', { detail: { giftQuantity: want } }));
      }
    } catch (err) {
      // Never break the cart; the discount still applies if the customer adds the gift themselves.
      console.warn('[A Marvellous Deal]', err);
    } finally {
      busy = false;
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(sync, 150));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
